import Center from "@/Components/Base/Center";
import Button from "@/Components/Button/Button";
import Spinner from "@/Components/Spinner/Spinner";
import PosTable from "@/Pages/Auth/Bestellingen/PosOrder/PosTable";
import { useSetPosOrderTable } from "@/shared/atoms/usePosOrderTable";
import usePosTableFilter, {
  filterPosTables,
  posTableFilterOptions,
} from "@/shared/atoms/usePosTableFilter";
import useCommon from "@/shared/hooks/useCommon";
import useActiveOrdersSessionsQuery from "@/shared/queries/orderssessions/useActiveOrdersSessionsQuery";
import useRestaurantMap from "@/shared/queries/restaurantMap/useRestaurantMap";
import useRestaurantMaps from "@/shared/queries/restaurantMap/useRestaurantMaps";
import { cn } from "@/shared/utils/cn";
import { asArray } from "@/shared/utils/helpers";
import { RestaurantTable } from "@/shared/utils/interfaces";
import { useEffect, useMemo, useState } from "react";
import { useSetWaiterView } from "@/shared/atoms/useWaiterView";
import { useSetMobileCartShow } from "@/shared/atoms/useMobileCartShow";

type WaiterTablesProps = {
  setTable?: (table: RestaurantTable) => void;
  active?: number | number[];
  disable?: number[];
  canChooseSession?: boolean;
  mustChooseSession?: boolean;
  forceNoSession?: boolean;
  compact?: boolean;
  statusFilter?: number;
  excludeTableIds?: number[];
};

const WaiterTables = ({
  setTable: setTableProp,
  active,
  disable,
  canChooseSession,
  mustChooseSession,
  forceNoSession,
  compact = false,
  statusFilter,
  excludeTableIds = [],
}: WaiterTablesProps) => {
  const t = useCommon();

  const [mapId, setMap] = useState<number | undefined>();
  const [tableFilter, setTableFilter] = usePosTableFilter();

  const filter = compact ? (statusFilter ?? 1) : tableFilter;
  const setGlobalTable = useSetPosOrderTable();
  const setWaiterView = useSetWaiterView();
  const setShowCartForMobile = useSetMobileCartShow();
  const setTable =
    setTableProp ??
    ((nextTable: RestaurantTable) => {
      setGlobalTable(nextTable);
      setWaiterView("menu");
      setShowCartForMobile(false);
    });

  const {
    isPending: isSessionsPending,
    data: sessions,
    refetch,
  } = useActiveOrdersSessionsQuery();
  const { data: maps, isPending: isRestaurantsMapsPending } =
    useRestaurantMaps();
  const mapsList = asArray(maps);
  const { data: map, isPending } = useRestaurantMap(mapId, mapId !== undefined);

  useEffect(() => {
    if (mapId === undefined && maps?.length > 0) {
      setMap(maps[0].item_id);
    }
  }, [maps, mapId]);

  useEffect(() => {
    refetch();
  }, []);

  const filtered = useMemo(
    () => filterPosTables(map?.tables, sessions, tableFilter),
    [map?.tables, sessions, tableFilter]
  );

  const tableCounts = {
    1: filterPosTables(map?.tables, sessions, 1).length,
    2: filterPosTables(map?.tables, sessions, 2).length,
    3: filterPosTables(map?.tables, sessions, 3).length,
  };

  if (isRestaurantsMapsPending || isSessionsPending) {
    return (
      <Center className="h-full w-full">
        <Spinner />
      </Center>
    );
  }

  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col",
        compact ? "overflow-hidden" : "h-full min-h-[32rem]"
      )}
    >
      <div className="shrink-0 space-y-3 pb-3">
        {mapsList.length > 1 ? (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {mapsList.map((item) => (
              <Button
                key={item.item_id}
                size="small"
                onClick={() => setMap(item.item_id)}
                className={cn(
                  "h-12 shrink-0 rounded-xl items-center flex border px-4 md:text-2xl font-semibold whitespace-nowrap",
                  item.item_id === mapId
                    ? "border-primary bg-primary text-white"
                    : "border-border bg-white text-dark"
                )}
              >
                {item.name}
              </Button>
            ))}
          </div>
        ) : null}

        {!compact ? (
          <div data-tour="waiter.filters" className="grid grid-cols-3 gap-2">
            {posTableFilterOptions.map((option) => (
              <Button
                key={option.value}
                size="small"
                onClick={() => setTableFilter(option.value)}
                className={cn(
                  "flex flex-col items-center justify-center rounded-xl md:text-2xl border font-semibold",
                  option.value === filter
                    ? "border-primary bg-primary text-white"
                    : "border-border bg-white text-dark"
                )}
              >
                <span>
                  {option.value === 1
                    ? t("all")
                    : option.value === 2
                      ? t("availableTable")
                      : t("occupiedTable")}
                </span>
                <span className="font-bold opacity-80">
                  {tableCounts[option.value]}
                </span>
              </Button>
            ))}
          </div>
        ) : null}
      </div>

      <div
        data-tour={compact ? undefined : "waiter.tables"}
        className={cn(
          "min-h-0 flex-1 overflow-y-auto rounded-2xl p-3 md:p-4",
          compact ? "min-h-64 bg-grey-blue-light" : "bg-white"
        )}
      >
        {mapId !== undefined && isPending ? (
          <Center className="h-full">
            <Spinner />
          </Center>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8">
            {filtered.map((table) => (
              <PosTable
                key={table.item_id}
                card
                setTable={setTable}
                table={table}
                active={active}
                disable={disable}
                canChooseSession={canChooseSession}
                mustChooseSession={mustChooseSession}
                forceNoSession={forceNoSession}
              />
            ))}
          </div>
        ) : (
          <Center flexDirection="column" gap="1rem" className="h-full">
            <h4 className="px-4 text-center text-xl font-semibold text-text-secondary">
              {t("noTablesFound")} {/* bu mesajı degiştir*/}
            </h4>
          </Center>
        )}
      </div>
    </div>
  );
};

export default WaiterTables;
